import moment from 'moment-timezone';
import copy from 'copy-to-clipboard';

import { toastMsg } from "../components/common/Toast/Toast";
import { DAY_EXPIRE, DAY_ORDER_EXPIRY_TIME, TZ } from '../constants/constants';
export const parseJSON = value => JSON.parse(JSON.stringify(value));

const formatAdaptiveNumericString = (num) => {
    if (!Number.isFinite(num)) return '0';
    if (num === 0) return '0';
    // Never rely on String(num) / num.toString() — those emit 1e+24 for large values.
    const plain = num.toLocaleString('en-US', {
        useGrouping: false,
        maximumFractionDigits: 20,
        minimumFractionDigits: 0,
    });
    return /e/i.test(plain) ? expandExponentialString(plain) : plain;
};

/** Expand "1e+24" / "4.8e-7" into a plain decimal string (no scientific notation). */
export const expandExponentialString = (value) => {
    const raw = String(value ?? '').trim().replace(/,/g, '');
    if (!raw) return '0';
    const match = raw.match(/^([+-]?)(\d+)(?:\.(\d+))?e([+-]?\d+)$/i);
    if (!match) return raw;

    const sign = match[1] === '-' ? '-' : '';
    const intDigits = match[2];
    const fracDigits = match[3] || '';
    const exponent = Number(match[4]);
    if (!Number.isFinite(exponent)) return raw;

    const digits = `${intDigits}${fracDigits}`;
    const decimalIndex = intDigits.length + exponent;

    if (decimalIndex <= 0) {
        return `${sign}0.${'0'.repeat(-decimalIndex)}${digits}`.replace(/0+$/, '').replace(/\.$/, '') || '0';
    }
    if (decimalIndex >= digits.length) {
        return `${sign}${digits}${'0'.repeat(decimalIndex - digits.length)}`;
    }
    const whole = digits.slice(0, decimalIndex).replace(/^0+(?=\d)/, '') || '0';
    const frac = digits.slice(decimalIndex).replace(/0+$/, '');
    return frac ? `${sign}${whole}.${frac}` : `${sign}${whole}`;
};

/** UI-safe decimal: never returns scientific notation. */
export const toPlainDecimalString = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    if (typeof value === 'number') {
        return formatAdaptiveNumericString(value);
    }
    const raw = String(value).trim().replace(/,/g, '');
    if (!raw) return '0';
    if (/e/i.test(raw)) {
        return expandExponentialString(raw);
    }
    if (!Number.isFinite(Number(raw))) return raw;
    return raw;
};

export const toFix = (value, n) => {
    if (value === null || value === undefined || value === '') return '';
    // Prefer string path so large decimal inputs keep digits (avoid Number precision loss).
    if (typeof value === 'string' && !/e/i.test(value.trim())) {
        const raw = value.trim().replace(/,/g, '');
        const decimals = n === null || n === undefined ? NaN : Number(n);
        if (!Number.isFinite(decimals) || decimals <= 0) {
            return raw.replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
        }
        const decPlaces = Math.floor(decimals);
        const parts = raw.split('.');
        const intPart = parts[0] || '0';
        let frac = parts[1] || '';
        if (frac.length > decPlaces) frac = frac.slice(0, decPlaces);
        frac = frac.replace(/0+$/, '');
        return frac ? `${intPart}.${frac}` : intPart;
    }

    const num = Number(value);
    if (!Number.isFinite(num)) return toPlainDecimalString(value);

    const decimals = n === null || n === undefined ? NaN : Number(n);
    if (!Number.isFinite(decimals) || decimals <= 0) {
        return formatAdaptiveNumericString(num);
    }

    const decPlaces = Math.floor(decimals);
    const normalized = formatAdaptiveNumericString(num);
    const parts = normalized.split('.');
    const intPart = parts[0];
    let frac = parts[1] || '';
    if (frac.length > decPlaces) {
        frac = frac.slice(0, decPlaces);
    }
    frac = frac.replace(/0+$/, '');
    if (!frac) return intPart;
    return `${intPart}.${frac}`;
};
export const inputDecimal = (value, decimals) => {
    // console.log('input ', value, decimals);
    let valData = value;
    if (Number(decimals) <= 0) {
        let getDicimal = value?.toString().split(".");
        valData = getDicimal[0];
        return valData;
    } else {
        if (
            value?.toString().includes(".") &&
            value?.toString().split(".")[1] !== ""
        ) {
            let getDicimal = value?.toString().split(".");
            let result = getDicimal[1].substring(0, Number(decimals));
            valData = getDicimal[0] + "." + result;
        }
        return valData;
    }
};
export const getDayOrderExpiryTime = () => {
    const currentDate = moment.tz(TZ).format('M/D/YYYY');
    return moment.tz(`${currentDate} ${DAY_EXPIRE}`, "M/D/YYYY h:mm a", TZ).diff(moment.tz(TZ), 'minutes');
}

/** Today 16:30 America/New_York as unix seconds (DVP expiration). */
export const getDayOrderExpireUnix = () => {
    const currentDate = moment.tz(TZ).format('M/D/YYYY');
    const expiry = moment.tz(
        `${currentDate} ${DAY_ORDER_EXPIRY_TIME}`,
        'M/D/YYYY HH:mm',
        TZ,
    );
    return expiry.isValid() ? expiry.unix() : 0;
};

/** Today 16:30 America/New_York as ISO string (create_order expire_at). */
export const getDayOrderExpireAt = () => {
    const currentDate = moment.tz(TZ).format('M/D/YYYY');
    return moment
        .tz(`${currentDate} ${DAY_ORDER_EXPIRY_TIME}`, 'M/D/YYYY HH:mm', TZ)
        .format();
};

export const copyToClipboard = (value) => {
    copy(value);
    toastMsg.success('Copied!');
};
export const getDateTimeEST = (dateTime = null, format = false) => {
    // let DateTime = moment().tz("America/New_York").format(format ? "DD/MM/YYYY HH:mm A" : "");
    // if (dateTime) DateTime = moment(dateTime).tz("America/New_York").format(format ? "DD/MM/YYYY HH:mm A" : "");
    // return DateTime
    let DateTime = moment().format(format ? "DD/MM/YYYY HH:mm A" : "");
    if (dateTime) DateTime = moment(dateTime).format(format ? "DD/MM/YYYY HH:mm A" : "");
    return DateTime;
}
export const getRandomColor = () => {
    // Generate a random hex color code
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
}

export const getTenDifferentColors = () => {
    const colors = [];
    for (let i = 0; i < 10; i++) {
        let color = getRandomColor();
        // Ensure the generated color is not already in the array
        while (colors.includes(color)) {
            color = getRandomColor();
        }
        colors.push(color);
    }
    return colors;
}

export const getColors = () => {
    const predefined = ["#00BE00", "#C52B2B", "#329BFF", "#FF5700", "#20325C"];
    return [...predefined, ...getTenDifferentColors()];
}